import { Form, InputNumber, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment, { type Moment } from 'moment';
import React from 'react';

import sharedStyles from '../../sharedStyles.module.scss';
import type {
  CleanReserveList,
  CleanServiceItem,
  MemberReservationLimit,
  TimeWithUnit,
} from '../../sharedTypes';
import type { ReservationDateStatus } from '../types';

const { Item } = Form;
const { Text } = Typography;

export const getTableColumns = (args: {
  isShowQuantityError: boolean;
  hideInputNumberErrorMsg: () => void;
}) => {
  const { isShowQuantityError, hideInputNumberErrorMsg } = args;
  const columns: ColumnsType<CleanServiceItem & { quantity: number | null }> = [
    {
      title: '項目',
      dataIndex: 'name',
    },
    {
      title: '價格',
      dataIndex: 'priceInfo',
      render: (_value, record) => {
        const { priceInfo } = record;
        switch (priceInfo.kind) {
          case 'Point':
            return `${priceInfo.pointPrice}點`;

          case 'Free':
            return '0元';

          default:
            return `${priceInfo.taiwanPayPrice}元`;
        }
      },
    },
    {
      title: '數量',
      dataIndex: 'quantity',
      render: (_value, record) => {
        const priceInfo = record.priceInfo;
        let min = 0;
        let max = 0;
        if (priceInfo.kind !== 'Free') {
          min = priceInfo.reservationLowerLimit;
          max = priceInfo.reservationUpperLimit;
          return (
            <Space direction="vertical">
              <Item
                name={record._id}
                noStyle
                initialValue={record.quantity ?? null}
              >
                <InputNumber
                  min={min}
                  max={max}
                  placeholder="請輸入數量"
                  className={sharedStyles.inputNumberWidth}
                  onChange={hideInputNumberErrorMsg}
                />
              </Item>
              {isShowQuantityError && <Text type="danger">請輸入數量</Text>}
            </Space>
          );
        } else {
          return (
            <Item name={`freeItem/${record._id}`} noStyle initialValue={1}>
              <div>1</div>
            </Item>
          );
        }
      },
    },
  ];

  return columns;
};

export const getQuantity = (
  serviceItem: CleanServiceItem,
  reservationList: CleanReserveList['clean'][0]['items']
) => {
  if (serviceItem.priceInfo.kind === 'Free') {
    return 1;
  }

  const foundReservation = reservationList.find(
    (reservation) => reservation.id === serviceItem._id
  );
  if (typeof foundReservation?.quantity === 'number') {
    return foundReservation.quantity;
  }

  return null;
};

export const checkIsDisabledDate = (args: {
  checkDate: Moment;
  reservationRule: {
    memberReservationLimit: MemberReservationLimit | undefined;
    canReserveAfter: TimeWithUnit | undefined;
    canReserveBefore: TimeWithUnit | undefined;
  };
  reservationList: CleanReserveList['clean'] | undefined;
  editingDate?: Moment;
  reservationDatesStatus: ReservationDateStatus['days'];
}) => {
  const {
    checkDate,
    reservationRule,
    reservationList,
    editingDate,
    reservationDatesStatus,
  } = args;

  const { canReserveAfter, canReserveBefore } = reservationRule;
  const canReserveAfterValue = canReserveAfter?.value;
  const canReserveAfterUnit = canReserveAfter?.unit;
  const canReserveBeforeValue = canReserveBefore?.value;
  const canReserveBeforeUnit = canReserveBefore?.unit;

  const dateFrom = moment().add(canReserveAfterValue, canReserveAfterUnit);
  const dateUntil = moment().add(canReserveBeforeValue, canReserveBeforeUnit);

  const foundDisabledDate = reservationList?.find((reservation) => {
    const { date } = reservation;
    return date.isSame(checkDate, 'date');
  });

  if (editingDate && editingDate.isSame(checkDate, 'date')) {
    return false;
  }

  let isDisabledDate = false;

  const currentCheckDate = checkDate.startOf('date').valueOf();
  const foundDate = reservationDatesStatus.find(
    (date) => date.timestamp === currentCheckDate
  );

  if (reservationDatesStatus && foundDate) {
    isDisabledDate = !foundDate.available;
  }

  return (
    foundDisabledDate !== undefined ||
    checkDate.isBetween(dateFrom, dateUntil, 'date', '[]') === false ||
    isDisabledDate
  );
};
